import { ArrowDown, DownloadIcon } from "@/assets/icons";
import { useMutation } from '@tanstack/react-query';
import { Spinner } from "@altimateai/lego";
import styles from "./styles.module.scss";
import { toast } from "react-toastify";
import {
  Button,
  ButtonProps,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";

const Loader = () => (
  <div className={styles.loader}>
    <Spinner size="sm" />
    <div className={styles.text}>Downloading… it might take a few minutes.</div>
  </div>
);

type DownloadTableProps = {
  handleDownload: () => Promise<void>;
  downloadOptions?: Array<{ label: string; handleClick: () => Promise<void> }>;
  buttonProps?: ButtonProps;
};

export const DownloadTable: React.FC<DownloadTableProps> = ({
  handleDownload,
  downloadOptions,
  buttonProps,
}) => {
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: async (downloadFn: () => Promise<void>) => {
      await downloadFn();
    },
    onError: (error: Error) => {
      toast.error("Download error. Please try again.");
      console.error("Download failed:", error);
    },
  });

  const handleMainDownload = () => mutate(handleDownload);
  const handleOptionDownload = (optionHandler: () => Promise<void>) => () => {
    mutate(optionHandler);
  };

  if (isLoading) return <Loader />;

  const hasOptions = downloadOptions && downloadOptions.length > 0;
  const buttonSize = buttonProps?.size || "default";

  if (!hasOptions) {
    return (
      <Button variant="secondary" onClick={handleMainDownload} size={buttonSize} {...buttonProps}>
        <DownloadIcon /> Download
      </Button>
    );
  }

  return (
    <Stack gap={"none"} align={"center"}>
      <Button
        variant="default"
        size={buttonSize}
        className={"al-rounded-r-none"}
        onClick={handleMainDownload}
        {...buttonProps}
      >
        <DownloadIcon /> Download
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size={buttonSize}
            className={"al-rounded-l-none al-border-l-0 !al-px-1 al-m-0"}
          >
            <ArrowDown />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {downloadOptions?.map((option, index) => (
            <DropdownMenuItem
              className={"al-cursor-pointer"}
              key={index}
              onClick={handleOptionDownload(option.handleClick)}
            >
              {option.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </Stack>
  );
};
